// App.js
import React, { useState, useEffect, Suspense, lazy, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import useLocalStorage from "use-local-storage";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { WorkspaceProvider, useWorkspace } from './context/WorkspaceContext';
import { QuotaProvider } from './context/QuotaContext';
import './firebase';
import { onAuthStateChanged } from "firebase/auth";
import { auth } from './firebase';
import axios from "axios";
import { LoadingIndicator } from "./components/Tools/LoadingIndicator";
import WorkspaceRoutes from "./WorkspaceRoutes";
import ConfirmModal from "./components/Modal/ConfirmModal";
import ReferralTracker, { storeReferralCode, trackReferralClick } from "./utils/referralUtils";

// Lazy load pages
const LoginPage = lazy(() => import("./pages/Auth/Login"));
const SignupPage = lazy(() => import("./pages/Auth/Signup"));
const LinkInBioPublic = lazy(() => import("./pages/Public/LinkInBioPublic"));
const SubscriptionOnboarding = lazy(() => import("./pages/Auth/SubscriptionGate/SubscriptionOnboarding"));

// Lazy load callback routes - these need to be accessible during onboarding
const Instagram = lazy(() => import("./pages/Protected/Callbacks/Instagram"));
const Google = lazy(() => import("./pages/Protected/Callbacks/Google"));
const Tiktok = lazy(() => import("./pages/Protected/Callbacks/Tiktok"));
const Facebook = lazy(() => import("./pages/Protected/Callbacks/Facebook"));
const X = lazy(() => import("./pages/Protected/Callbacks/X"));
const Threads = lazy(() => import("./pages/Protected/Callbacks/Threads"));
const Linkedin = lazy(() => import("./pages/Protected/Callbacks/Linkedin"));
const Pinterest = lazy(() => import("./pages/Protected/Callbacks/Pinterest"));
const Reddit = lazy(() => import("./pages/Protected/Callbacks/Reddit"));
const Stripe = lazy(() => import("./pages/Protected/Callbacks/Stripe"));

// Define React Router future flags
const routerFutureConfig = {
  v7_startTransition: true,
  v7_relativeSplatPath: true
};

// Fallback component for lazy loading
const PageLoader = () => <div className="flex justify-center items-center min-h-screen">
  <LoadingIndicator showLogo={true} />
</div>;

// Helper component to handle referral route
const ReferralHandler = () => {
  const { referralCode } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    console.log('ReferralHandler: processing referral code:', referralCode);
    
    if (referralCode) {
      // Store the referral code - this function now checks for duplicates
      storeReferralCode(referralCode);
      
      // After a short delay to ensure any tracking completes, redirect to signup
      setTimeout(() => {
        navigate('/signup', { replace: true });
      }, 300);
    }
  }, [referralCode, navigate]);

  return <PageLoader />;
};

// Helper component to render modal within context
const AppContent = () => {
  const { 
    confirmModalState, 
    confirmWorkspaceChange, 
    cancelWorkspaceChange 
  } = useWorkspace();

  return (
    <>
      <WorkspaceRoutes />
      <ConfirmModal 
        isOpen={confirmModalState.isOpen}
        onClose={cancelWorkspaceChange}
        onConfirm={confirmWorkspaceChange}
        title={confirmModalState.title}
        message={confirmModalState.message}
      />
    </>
  );
}

const App = () => {
  const [theme] = useLocalStorage("theme", 'light');
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Memoize the auth state handler to prevent unnecessary re-renders
  const handleAuthStateChange = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      setUserLoggedIn(false);
      setIsLoading(false);
      return;
    }
    
    try {
      // Force a token refresh if needed - the 'true' parameter forces refresh
      const token = await firebaseUser.getIdToken(true);
      
      // Check if the user exists in the backend
      try {
        await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/auth/user/getUser`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setUserLoggedIn(true);
      } catch (err) {
        // If there's an auth error (e.g., token expired despite refresh attempt)
        if (err.response && (err.response.status === 401 ||
          err.response.data?.error?.code === 'auth/id-token-expired')) {
          // Try one more time with a forced refresh
          try {
            // Clear auth state and attempt re-login
            await auth.signOut();
            setUserLoggedIn(false);
            // The user will need to login again
            console.error("Authentication session expired. Please log in again.");
          } catch (innerErr) {
            console.error("Error during auth cleanup:", innerErr);
            setUserLoggedIn(false);
          }
        } else {
          console.error("Error verifying user:", err);
          setUserLoggedIn(false);
        }
      }
    } catch (err) {
      console.error("Error getting ID token:", err);
      setUserLoggedIn(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, handleAuthStateChange);
    return () => unsubscribe();
  }, [handleAuthStateChange]);

  if (isLoading) {
    return <LoadingIndicator showLogo={true} className="h-screen w-screen" />;
  }

  return (
    <div className="App min-h-screen" data-theme={`${theme}`}>
      <BrowserRouter future={routerFutureConfig}>
        <ReferralTracker userLoggedIn={userLoggedIn}>
          <ToastContainer
            position="bottom-right"
            autoClose={7000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss={false}
            draggable
            pauseOnHover
            theme={theme}
          />

          <div className={`pages`}>
            {/* Public routes that don't require authentication */}
            <Routes>
              {/* Public Link in Bio route */}
              <Route path="link/:customUrl" element={
                <Suspense fallback={<PageLoader />}>
                  <LinkInBioPublic />
                </Suspense>
              } />
              
              {/* Explicit route for referral links with a dedicated handler */}
              <Route path="ref/:referralCode" element={
                userLoggedIn ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <ReferralHandler />
                )
              } />

              {/* If logged in, show authenticated routes */}
              <Route path="/*" element={
                userLoggedIn ? (
                  <WorkspaceProvider>
                    <QuotaProvider>
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          {/* Callback routes - accessible to authenticated users regardless of subscription */}
                          <Route path="/callback/instagram" element={<Instagram />} />
                          <Route path="/callback/google" element={<Google />} />
                          <Route path="/callback/tiktok" element={<Tiktok />} />
                          <Route path="/callback/facebook" element={<Facebook />} />
                          <Route path="/callback/x" element={<X />} />
                          <Route path="/callback/threads" element={<Threads />} />
                          <Route path="/callback/linkedin" element={<Linkedin />} />
                          <Route path="/callback/pinterest" element={<Pinterest />} />
                          <Route path="/callback/reddit" element={<Reddit />} />
                          <Route path="/callback/stripe" element={<Stripe />} />
                          
                          {/* Onboarding route - accessible to authenticated users without subscription */}
                          <Route path="/onboarding" element={<SubscriptionOnboarding />} />
                          
                          {/* All other routes go through workspace protection */}
                          <Route path="/*" element={<AppContent />} />
                        </Routes>
                      </Suspense>
                    </QuotaProvider>
                  </WorkspaceProvider>
                ) : (
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/signup" element={<SignupPage />} />
                      <Route path="/onboarding" element={<SubscriptionOnboarding />} />
                      {/* If not logged in, redirect everything else to /login */}
                      <Route path="*" element={<Navigate to="/login" replace />} />
                    </Routes>
                  </Suspense>
                )
              } />
            </Routes>
          </div>
        </ReferralTracker>
      </BrowserRouter>
    </div>
  );
};

export default App;
